import { useStore } from "vuex"
import { ref, computed } from 'vue'

const FlowSteps = {
    CANCERFORUM: "CANCERFORUM",
    FRIVILLIG: "FRIVILLIG",
    SFL: "SFL",
    MENO: "MENO"
}

const DeletionMethods = {
    SOFT_DELETE: "soft-delete",
    HARD_DELETE: "hard-delete",
}

export default {
    name: "DeleteProfileFlow",
    props: {
        userProfileId: {
            type: String,
            required: true
        },
        userData: {
            type: Object,
            required: true
        },
    },
    setup(props) {           
        const store = useStore()

        const isSubmitting = computed(() => store.state.main.is_submitting)
        const displayError = ref(false)

        const steps = []
        if (props.userData.hasCancerforumProfile) steps.push(FlowSteps.CANCERFORUM)
        if (props.userData.hasFrivilligProfile) steps.push(FlowSteps.FRIVILLIG)
        if (props.userData.hasStafetForLivetProfile) steps.push(FlowSteps.SFL)
        if (props.userData.hasMenopauseProfile) steps.push(FlowSteps.MENO)    
        if (!steps.length)
            throw new Error("Must have at least one step in flow")

        const currentStepIndex = ref(0)
        const currentStep = computed(() => steps[currentStepIndex.value])
        const isFirstStep = computed(() => currentStepIndex.value === 0)
        const isLastStep = computed(() => currentStepIndex.value === steps.length - 1)

        const selectedCancerforumDeleteSetting = ref(null)
        const isCancerforumDeleteSettingValid = computed(() => !!selectedCancerforumDeleteSetting.value)

        const isCurrentStepValid = computed(() => {
            switch (currentStep.value) {
                case FlowSteps.CANCERFORUM:
                    return isCancerforumDeleteSettingValid.value
                case FlowSteps.FRIVILLIG: // No input required for Frivillig
                case FlowSteps.SFL: // No input required for SFL
                case FlowSteps.MENO: // No input required for Meno                     
                default: 
                    return true
            }
        })

        const getLabel = (labelKey) =>
            store.getters['labels/getLabel']({
                labelCollection: 'DeleteProfileFlow',
                labelKey: labelKey
            })
        
        const getStepTitle = (step) => {
            switch (step) {
                case FlowSteps.CANCERFORUM:
                    return getLabel('cancerforumTitle')
                case FlowSteps.FRIVILLIG:
                    return getLabel('frivilligTitle')
                case FlowSteps.SFL:
                    return getLabel('sflTitle')
                case FlowSteps.MENO:
                   return getLabel('menoTitle')
                default:
                    return ""
            }
        }

        const nextStepLabel = computed(() => {

            if (isLastStep.value)
                return ""

            const nextStep = steps[currentStepIndex.value + 1]

            switch (nextStep) {
                case FlowSteps.FRIVILLIG:
                    return getLabel('continueToFrivilligLabel')
                case FlowSteps.SFL:
                    return getLabel('continueToSflLabel')
                case FlowSteps.CANCERFORUM:
                    return getLabel('continueToCancerforumLabel')
                case FlowSteps.MENO:
                    return getLabel('continueToMemoLabel')
                default:
                    throw new Error(`Invalid next step: ${nextStep}`)
            }
        })

        const scrollToTop = () => {
            scrollTo(0, 0)
        }

        const cancel = () => {
            history.back()
        }

        const previousStep = () => {
            currentStepIndex.value--
            scrollToTop()
        }
        
        const nextStep = () => {

            if (!isCurrentStepValid.value) {
                displayError.value = true
                return
            }
            
            displayError.value = false
            currentStepIndex.value++
            scrollToTop()
        }

        const submit = async () => {
            if (!isCurrentStepValid.value) {
                displayError.value = true
                return
            }
            
            displayError.value = false

            store.dispatch('main/deleteUserProfile', {
                userProfileId: props.userProfileId,
                body: {
                    isCancerforumSoftDelete: selectedCancerforumDeleteSetting.value === DeletionMethods.SOFT_DELETE
                }
            })
            .then((redirectUrl) => window.location.href = redirectUrl)
        }

        const decodeHtml = (htmlRaw) => {
            const txt = document.createElement('textarea')
            txt.innerHTML = htmlRaw
            return txt.value
        }

        return {
            getLabel,
            decodeHtml,
            isSubmitting,
            displayError,
            steps,
            currentStep,
            currentStepIndex,
            isFirstStep,
            isLastStep,
            FlowSteps,
            DeletionMethods,
            nextStepLabel,
            getStepTitle,
            cancel,
            previousStep,
            nextStep,
            submit,
            selectedCancerforumDeleteSetting,
            isCancerforumDeleteSettingValid,
        }
    }
}